import { Avatar, Box, Chip, Stack, Typography } from '@mui/material';
import { useAppSelector } from '@redux/hooks';
import { selectMemberDetails } from '@redux/teams';
import { useParams } from 'react-router-dom';
import { getHappinessColor } from '@utils';
import { useEffect } from 'react';
import { logEvent } from '@services';
import { PageTitle } from 'constants/pageTitle';
import { useTitle } from '@hooks';
import { MemberEvaluationSkeleton } from '@components';

export const MemberEvaluation = () => {
  const { evaluationId } = useParams();
  const memberDetails = useAppSelector(selectMemberDetails);
  const member = memberDetails.data?.member;
  const evaluation = member?.evaluations.find((e) => e.id === evaluationId);
  const setPageTitle = useTitle();

  useEffect(() => {
    if (memberDetails.data) {
      setPageTitle(
        `${memberDetails.data.member.fullName} - ${PageTitle.EVALUATIONS}`,
      );
      logEvent('member_evaluation_details_page_visit');
    }
  }, [setPageTitle, memberDetails.data]);

  if (!evaluation || !member) return <MemberEvaluationSkeleton />;

  const renderMark = (label: string, value: string) => (
    <Stack direction="row" alignItems="center" spacing={2} pl={{ tablet: 2 }}>
      <Typography fontWeight={500} width={180} maxWidth={120}>
        {label}
      </Typography>
      <Chip
        label={value}
        sx={{
          bgcolor: value
            ? getHappinessColor(
                value.toLowerCase() === 'exceeds'
                  ? 10
                  : value.toLowerCase() === 'successful'
                    ? 8
                    : 4,
              )
            : '#888888',
          textTransform: 'capitalize',
          borderRadius: 2,
          color: '#fff',
          fontWeight: 500,
        }}
      />
    </Stack>
  );

  return (
    <Box px={{ tablet: 3 }} pb={{ tablet: 3 }} maxWidth={550} m="auto">
      <Typography
        variant="h2"
        typography={{ xs: 'h3', tablet: 'h2' }}
        fontWeight={{ tablet: 600 }}
        mb={0}
        ml={{ tablet: 2 }}
      >
        Your feedback on {member.fullName}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        mb={3}
        ml={{ tablet: 2 }}
        display="block"
      >
        {new Date(evaluation.evaluationDate).toLocaleDateString(undefined, {
          month: 'long',
          year: 'numeric',
        })}
      </Typography>

      <Box
        mx={{ xs: -2, tablet: 0 }}
        sx={{ p: 2, mb: 3, backgroundColor: 'highlight.neutral' }}
      >
        <Stack
          direction="column"
          spacing={2}
          alignItems="flex-start"
          sx={{ border: 'none' }}
        >
          <Box display="flex" flexDirection="row" gap={2} alignItems="center">
            <Avatar
              sx={{ borderRadius: 2, height: 52, width: 52 }}
              src={member.avatarUrl}
            />
            <Typography variant="h3" fontWeight={300} lineHeight={1.5}>
              How would you rate quality of work of {member.fullName}?
            </Typography>
          </Box>
          <Chip
            label={evaluation.feedback.qualityOfWork}
            sx={{
              borderRadius: 2,
              mt: 1,
              bgcolor:
                evaluation.feedback.qualityOfWork && evaluation.feedback
                  ? getHappinessColor(
                      evaluation.feedback.qualityOfWork.toLocaleLowerCase() ===
                        'exceeds'
                        ? 10
                        : evaluation.feedback.qualityOfWork.toLocaleLowerCase() ===
                            'successful'
                          ? 8
                          : 4,
                    )
                  : '#888888',
              textTransform: 'capitalize',
              color: '#fff',
              fontWeight: 500,
            }}
          />
        </Stack>
      </Box>

      <Stack spacing={2} mb={4}>
        {renderMark(
          'Functional expertise',
          evaluation.feedback.functionalExpertise,
        )}
        {renderMark('Communication', evaluation.feedback.communication)}
        {renderMark(
          'Initiative and ownership',
          evaluation.feedback.initiativeAndOwnership,
        )}
        {renderMark(
          'Leadership and mentorship',
          evaluation.feedback.leadershipAndMentorship,
        )}
      </Stack>

      <Box ml={{ tablet: 2 }}>
        <Typography fontWeight={500} mb={1}>
          What improvement points or achievements would you like to highlight?
        </Typography>
        <Typography color="text.secondary" whiteSpace="pre-wrap">
          {evaluation.feedback.comments || '—'}
        </Typography>
      </Box>
    </Box>
  );
};
